/* eslint-disable camelcase */
import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import _omit from 'lodash/omit';
import { Modal, MODAL_SIZES } from 'ui/modal';
import noop from 'utils/noop';
import { getFieldsWithSuggestions, getSampleDataFromJson } from 'modules/catalogFieldTypes/helpers';
import { UPDATE_CATALOG_ITEM_METHOD } from 'modules/catalogTable/constants';
import {
  getNewFieldsFromJson,
  checkIfNestedObject,
  isSubmitDisabled,
} from './CatalogFormJSON.utils';
import ModalHeader from './ModalHeader';
import ModalActions from './ModalActions';
import ModalContent from './ModalContent';

// type Props = {
//   /** Function when closing modal, triggers fetch to update list */
//   closeUpdateItemModal: () => void,
//   /** Is update catalog modal open or not */
//   updateCatalogIsOpen: boolean,
//   /** Function to call to create catalog item/value */
//   updateCatalogItemRequest: (*) => *,
//   /** String for current table */
//   tableName: string,
//   /** Optional string for current key */
//   tableKey: ?string,
//   /** Optional string for current value */
//   tableValue: ?string,
//   /** Boolean for CatalogQueries flag */
//   isCatalogCollectionsFlagOn: boolean,
//   /** Is form in process of being submitted or not (saved in redux store) */
//   isSubmitting: ?boolean,
//   /** Callback function when field definition is skiped. */
//   skipFieldDefinition: () => void,
//   /** Callback function when field definition is skip and uploaded to catalog. */
//   skipFieldDefinitionAndUploadToCatalog: () => void,
//   /** Function to call to update field types and then update/create catalog item via JSON */
//   updateFieldsAndCatalogItemRequest: (*) => void,
//   /** Object representing defined fields where key is field name and value is field type e.g., "string", "geo_location" */
//   definedFields: Object,
// };

// type State = {
//   tableKey: string,
//   tableValue: string,
//   step: number,
//   skipDefine: boolean,
//   newJsonFields: Array<*>,
//   fields: Object,
//   isSubmitting: ?boolean,
//   suggestedTypes: ?Object,
//   nestedFieldsWarning: boolean,
// };

const INIT_STATE = {
  tableKey: '',
  tableValue: '{}',
  step: 1,
  skipDefine: false,
  newJsonFields: [],
  fields: {},
  nestedFieldsWarning: false,
};

export default class CatalogFormJSON extends React.Component {
  static defaultProps = {
    skipFieldDefinition: noop,
    skipFieldDefinitionAndUploadToCatalog: noop,
  };

  constructor(props) {
    super(props);
    this.state = {
      ...INIT_STATE,
    };
  }

  componentDidUpdate(prevProps) {
    const { tableKey, tableValue } = this.props;
    if (prevProps.tableKey !== tableKey) {
      // if you do not do this the modal will animate twice
      setTimeout(() => this.setState({ tableKey }), 0);
    }
    if (prevProps.tableValue !== tableValue) {
      // if you do not do this the modal will animate twice
      setTimeout(() => this.setState({ tableValue }), 0);
    }
  }

  getNewFieldsFromJson = (val /*: string */) => {
    const { definedFields } = this.props;
    return getNewFieldsFromJson(val, definedFields);
  };

  handleKeyChange = (tableKey) => {
    this.setState({ tableKey });
  };

  checkIfNestedObject = checkIfNestedObject;

  handleValueChange = (tableValue) => {
    const newJsonFields = this.getNewFieldsFromJson(tableValue);
    const nestedFieldsWarning = this.checkIfNestedObject(newJsonFields, tableValue);
    this.setState({
      tableValue,
      newJsonFields,
      nestedFieldsWarning,
    });
  };

  handleJsonOnUpdate = ({ updated_src }) => {
    const jsonString = JSON.stringify(updated_src);
    const newJsonFields = this.getNewFieldsFromJson(jsonString);
    const nestedFieldsWarning = this.checkIfNestedObject(newJsonFields, jsonString);
    this.setState({
      tableValue: JSON.stringify(updated_src),
      newJsonFields,
      nestedFieldsWarning,
    });
  };

  handleSubmit = () => {
    const { fields } = this.state;
    const { tableName } = this.props;

    if (_isEmpty(fields)) {
      // only update catalog item
      this.props.updateCatalogItemRequest({ ...this.state, tableName });
    } else {
      // update fields and item
      this.props.updateFieldsAndCatalogItemRequest({ ...this.state, tableName });
    }
    this.handleCloseModal();
  };

  handleSubmitToCatalog = () => {
    this.props.skipFieldDefinitionAndUploadToCatalog();
    this.handleSubmit();
  };

  handleCloseModal = () => {
    const { tableKey, tableValue } = this.props;
    this.setState({
      ...INIT_STATE,
      tableKey,
      tableValue,
    });
    this.props.closeUpdateItemModal();
  };

  handleSkipDefinition = () => {
    this.props.skipFieldDefinition();
    this.setState({ fields: {} });
  };

  handleStepAndSkip = (step /*: number */, skipDefine /*: ?boolean */) => {
    const prevStep = this.state.step;
    this.setState({ step, skipDefine: skipDefine === undefined ? false : skipDefine });
    switch (step) {
      case 1:
        this.handleSkipDefinition();
        break;
      case 2:
        // do not get auto-suggestions if going backwards from step 3 to step 2 (should instead preserve whatever changes the user has made to the fields)
        if (!skipDefine && prevStep !== 3) {
          const { fields, newJsonFields, tableValue } = this.state;
          const rows = getSampleDataFromJson({ newFields: newJsonFields, tableValue });
          const { suggestedTypes, updatedFields } = getFieldsWithSuggestions({ rows, fields });
          this.setState({ suggestedTypes, fields: updatedFields });
          return;
        }
        if (skipDefine) {
          this.handleSkipDefinition();
        }
        break;
      case 3:
      default:
        if (skipDefine) {
          this.handleSkipDefinition();
        }
    }
  };

  isDisabled = () => {
    const { isSubmitting, tableKey } = this.state;
    return isSubmitDisabled(isSubmitting, tableKey);
  };

  handleOnChangeFieldTypesDropdown = (val, key) => {
    const { fields } = this.state;
    if (val) {
      this.setState({ fields: { ...fields, [key]: val } });
    } else {
      this.setState({ fields: _omit(fields, key) });
    }
  };

  render() {
    const {
      tableKey,
      tableValue,
      step,
      skipDefine,
      newJsonFields,
      fields,
      suggestedTypes,
      nestedFieldsWarning,
    } = this.state;
    const { tableName, isCatalogCollectionsFlagOn, updateCatalogIsOpen } = this.props;
    const isDisabled = this.isDisabled();
    const steps = [
      {
        label: 'Create JSON',
        onClick: () => this.handleStepAndSkip(1, skipDefine),
      },
      {
        label: 'Define fields',
        onClick: () => this.handleStepAndSkip(2, false),
      },
      {
        label: 'Upload',
        icon: 'flag checkered',
        onClick: () => this.handleStepAndSkip(3, skipDefine),
      },
    ];

    return updateCatalogIsOpen ? (
      <Modal
        size={MODAL_SIZES.LARGE}
        id="catalogFormJsonModal"
        isModalOpen={updateCatalogIsOpen}
        onClose={this.handleCloseModal}
      >
        <ModalHeader
          tableName={tableName}
          isCatalogCollectionsFlagOn={isCatalogCollectionsFlagOn}
          steps={steps}
          step={step}
          skipDefine={skipDefine}
          updateMethod={UPDATE_CATALOG_ITEM_METHOD.JSON}
        />
        <ModalContent
          isCatalogCollectionsFlagOn={isCatalogCollectionsFlagOn}
          steps={steps}
          step={step}
          skipDefine={skipDefine}
          tableKey={tableKey}
          tableValue={tableValue}
          updateMethod={UPDATE_CATALOG_ITEM_METHOD.JSON}
          handleKeyChange={this.handleKeyChange}
          handleValueChange={this.handleValueChange}
          handleJsonOnUpdate={this.handleJsonOnUpdate}
          nestedFieldsWarning={nestedFieldsWarning}
          newFields={newJsonFields}
          fields={fields}
          suggestedTypes={suggestedTypes}
          dropdownOnChangeHandler={this.handleOnChangeFieldTypesDropdown}
        />
        <ModalActions
          isCatalogCollectionsFlagOn={isCatalogCollectionsFlagOn}
          isDisabled={isDisabled}
          handleStepAndSkip={this.handleStepAndSkip}
          handleSubmitToCatalog={this.handleSubmitToCatalog}
          handleSubmit={this.handleSubmit}
          step={step}
          skipDefine={skipDefine}
          fields={fields}
          hasNewFields={newJsonFields.length > 0}
          nestedFieldsWarning={nestedFieldsWarning}
        />
      </Modal>
    ) : null;
  }
}
